import Page from 'attendee/theme/Page'
import React, {useMemo} from 'react'
import {EventListItem} from 'attendee/EventListItem'
import {useAttendeeEventsList} from 'attendee/providers/AttendeeEventsListProvider'
import styled from 'styled-components'
import {Link} from 'react-router-dom'
import {LocalLink} from 'i18n/LocalLink'
import CounterShape from 'assets/img/shape/counter-shape.png'
import {routes} from 'routes/routes'
import home1 from 'assets/custom_images/home1.png'
import home2 from 'assets/custom_images/home2.png'
import CorporateFareIcon from '@mui/icons-material/CorporateFare'
import {useAuth} from 'attendee/Auth/Providers/AuthProvider'
import LiveHelpIcon from '@mui/icons-material/LiveHelp'
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates'
import {WhyOnlineEvent} from 'attendee/theme/WhyOnlineEvent'
import {Sticks} from 'attendee/Sticks'
import {Helmet} from 'react-helmet'
import {DEFAULT_OG_IMAGE, SITE_ORIGIN} from 'seo/siteMeta'
import {useI18n} from 'i18n/I18nContext'
import {withLocalePath} from 'i18n/localePaths'
import {AppLocale, SUPPORTED_LOCALES, appLocaleToBcp47} from 'i18n/locales'

export function Index() {
  const {events} = useAttendeeEventsList()
  const {user} = useAuth()
  const {locale, t} = useI18n()
  const pageUrl = `${SITE_ORIGIN}${withLocalePath(locale, '/')}`
  const pricesUrl = `${SITE_ORIGIN}${withLocalePath(locale, routes.prices)}`
  const bcp47 = appLocaleToBcp47(locale)
  const siteInLanguages = useMemo(() => {
    const ordered: AppLocale[] = [
      'bg',
      ...SUPPORTED_LOCALES.filter((l) => l !== 'bg'),
    ]
    return ordered.map(appLocaleToBcp47)
  }, [])

  const homeLdJson = useMemo(
    () =>
      JSON.stringify({
        '@context': 'https://schema.org',
        '@graph': [
          {
            '@type': 'WebPage',
            '@id': `${pageUrl}#webpage`,
            url: pageUrl,
            name: t('home.ogTitle'),
            description: t('home.metaDescription'),
            inLanguage: bcp47,
            isPartOf: {'@id': `${SITE_ORIGIN}/#website`},
          },
          {
            '@type': 'WebSite',
            '@id': `${SITE_ORIGIN}/#website`,
            url: `${SITE_ORIGIN}/`,
            name: 'Eventa.tv',
            description: t('home.metaDescription'),
            inLanguage: siteInLanguages,
            publisher: {'@id': `${SITE_ORIGIN}/#organization`},
          },
          {
            '@type': 'Organization',
            '@id': `${SITE_ORIGIN}/#organization`,
            name: 'Eventa.tv',
            url: `${SITE_ORIGIN}/`,
            description: t('home.metaDescription'),
            logo: `${SITE_ORIGIN}/logo512.png`,
          },
          {
            '@type': 'SoftwareApplication',
            name: 'Eventa.tv',
            applicationCategory: 'BusinessApplication',
            operatingSystem: 'Web',
            url: pageUrl,
            description: t('home.metaDescription'),
            inLanguage: bcp47,
            offers: {
              '@type': 'Offer',
              url: pricesUrl,
            },
          },
        ],
      }),
    [bcp47, pageUrl, pricesUrl, siteInLanguages, t],
  )

  return (
    <Page>
      <>
        <Helmet>
          <title>{t('home.metaTitle')}</title>
          <meta name="description" content={t('home.metaDescription')} />
          <link rel="canonical" href={pageUrl} />
          <meta property="og:type" content="website" />
          <meta property="og:title" content={t('home.ogTitle')} />
          <meta property="og:description" content={t('home.ogDescription')} />
          <meta property="og:url" content={pageUrl} />
          <meta property="og:image" content={DEFAULT_OG_IMAGE} />
          <meta
            property="og:image:secure_url"
            content={DEFAULT_OG_IMAGE}
          />
          <meta property="og:image:type" content="image/jpeg" />
          <meta property="og:image:width" content="1200" />
          <meta property="og:image:height" content="630" />
          <meta property="og:image:alt" content={t('home.ogImageAlt')} />
          <meta name="twitter:card" content="summary_large_image" />
          <meta name="twitter:title" content={t('home.ogTitle')} />
          <meta name="twitter:description" content={t('home.ogDescription')} />
          <meta name="twitter:image" content={DEFAULT_OG_IMAGE} />
          <meta name="twitter:image:alt" content={t('home.ogImageAlt')} />
          <script type="application/ld+json">{homeLdJson}</script>
        </Helmet>

        <section className="event__schedule-area dark_light_class pt-55 pb-35 ">
          <div className="container">
            <div className="section__title-wrapper is-left mb-50">
              <span className="section__back-title">
                {t('home.scheduleBackTitle')}
              </span>
              <span className="section__subtitle">
                {t('home.scheduleSubtitle')}
              </span>
              <h1 className="section__title">
                {t('home.scheduleTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('home.scheduleTitleHighlight')}
                </span>
              </h1>
            </div>

            <div className="row">
              {events.length > 0 &&
                events.map((event, index) => (
                  <EventListItem event={event} index={index} key={index} />
                ))}
            </div>
          </div>
        </section>

        <section className="event__schedule-area dark_light_class pt-55 pb-35 ">
          <div className="container">
            <div className="section__title-wrapper is-left mb-50">
              <span className="section__back-title">
                {t('home.sticksBackTitle')}
              </span>
              <span className="section__subtitle">
                {t('home.sticksSubtitle')}
              </span>
              <h2 className="section__title">
                {t('home.sticksTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('home.sticksTitleHighlight')}
                </span>
              </h2>
            </div>

            <div className="row">
              <Sticks />
            </div>
          </div>
        </section>

        <section className="counter__area include__bg pt-150 pb-120 counter__overlay fix">
          <div className="counter__shape">
            <img
              src={CounterShape}
              style={{width: 'auto', height: 'auto'}}
              alt=""
              aria-hidden="true"
            />
          </div>
          <div className="container">
            <div className="row" style={{alignItems: 'center'}}>
              <HelpText>{t('home.bannerHelpTitle')}</HelpText>

              <HelpButtonHolder>
                <HelpButton className="input__btn" to={routes.info}>
                  <TipsAndUpdatesIcon />{' '}
                  <span>{t('home.bannerWhatIsEventa')}</span>
                </HelpButton>
                <HelpButton className="input__btn" to={routes.help}>
                  <LiveHelpIcon /> <span>{t('home.bannerHelp')}</span>
                </HelpButton>
              </HelpButtonHolder>
            </div>
          </div>
        </section>

        <section
          id="homeabout"
          className={`about__area p-relative pt-150 dark_light_class `}
        >
          <div className="container">
            <div className="row align-items-center">
              <div className="col-xxl-6 col-xl-6 col-lg-6">
                <div className="about__content-wrapper mb-60">
                  <div className="section__title-wrapper mb-80">
                    <span className="section__back-title">
                      {t('home.aboutBackTitle')}
                    </span>
                    <span className="section__subtitle">
                      {t('home.aboutSubtitle')}
                    </span>
                    <h2 className="section__title">
                      {t('home.aboutTitleBefore')}{' '}
                      <span className="text__highlight">
                        {t('home.aboutTitleHighlight')}
                      </span>
                    </h2>
                  </div>
                  <div className="about__content">
                    <div className="about__text">
                      <h3
                        style={{
                          fontSize: 'var(--bd-fs-h4)',
                          color: 'var(--clr-text-1)',
                        }}
                      >
                        {t('home.aboutLead')}
                      </h3>
                      <p className="mb-30"> {t('home.aboutBody1')}</p>
                      <p>{t('home.aboutBody2')}</p>
                    </div>
                    <div className="about__location-info mb-80">
                      <div className="about__location-inner">
                        <div className="about__location-icon">
                          <Link to="#" aria-label="nowhere">
                            <i className="fa-solid fa-location-dot"></i>
                          </Link>
                        </div>
                        <div className="about__location-address">
                          <h4>{t('home.aboutLocationTitle')}</h4>
                          <span>
                            {t('home.aboutLocationAnywhere')} <br />{' '}
                            {t('home.aboutLocationAnytime')}
                          </span>
                        </div>
                      </div>
                      <div className="pluse__status">
                        <span className="dot"></span>
                        <span className="text">
                          {t('home.aboutOnlineLabel')}
                        </span>
                      </div>
                    </div>
                  </div>
                  {user && (
                    <LocalLink to={routes.info} className="fill__btn">
                      {t('home.aboutLearnMore')}
                      <i className="fa-regular fa-angle-right"></i>
                    </LocalLink>
                  )}
                  {!user && (
                    <LocalLink to={routes.register} className="fill__btn">
                      {t('home.aboutRegisterCta')}
                      <i className="fa-regular fa-angle-right"></i>
                    </LocalLink>
                  )}
                </div>
              </div>
              <div className="col-xxl-6 col-xl-6 col-lg-6">
                <div className="about__thumb-wrapper mb-60 p-relative">
                  <div className="about__thumb-1 text-end w-img">
                    <img
                      src={home2}
                      style={{width: '100%', height: 'auto'}}
                      alt={t('home.aboutImageAlt')}
                    />
                    <div className="panel wow"></div>
                  </div>
                  <div className="about__thumb-2 w-img">
                    <img
                      src={home1}
                      style={{width: '100%', height: 'auto'}}
                      alt={t('home.aboutImageAlt')}
                    />
                    <div className="panel wow"></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="dark_light_class pt-55 pb-35">
          <div className="container">
            <div className="section__title-wrapper is-left mb-35">
              <span className="section__back-title">
                {t('home.testimonialsBackTitle')}
              </span>
              <span className="section__subtitle">
                {t('home.testimonialsSubtitle')}
              </span>
              <h2 className="section__title">
                {t('home.testimonialsTitleBefore')}{' '}
                <span className="text__highlight">
                  {t('home.testimonialsTitleHighlight')}
                </span>
              </h2>
            </div>
            <TestimonialsGrid>
              <TestiCard>
                {t('home.testimonial1Text')}
                <TestiAuthor>{t('home.testimonial1Author')}</TestiAuthor>
              </TestiCard>
              <TestiCard>
                {t('home.testimonial2Text')}
                <TestiAuthor>{t('home.testimonial2Author')}</TestiAuthor>
              </TestiCard>
              <TestiCard>
                {t('home.testimonial3Text')}
                <TestiAuthor>{t('home.testimonial3Author')}</TestiAuthor>
              </TestiCard>
            </TestimonialsGrid>
          </div>
        </section>

        {/* Newsletter + partners blocks removed; re-enable with i18n keys when needed. */}

        <WhyOnlineEvent />

        <section className="counter__area include__bg pt-150 pb-120 counter__overlay fix">
          <div className="counter__shape">
            <img
              src={CounterShape}
              style={{width: 'auto', height: 'auto'}}
              alt=""
              aria-hidden="true"
            />
          </div>
          <div className="container">
            <div className="row" style={{alignItems: 'center'}}>
              <HelpText>{t('home.orgCtaTitle')}</HelpText>

              <HelpButtonHolder>
                <HelpButton
                  className="input__btn"
                  to={routes.organization.login}
                >
                  <CorporateFareIcon /> <span>{t('home.orgCtaRegister')}</span>
                </HelpButton>
              </HelpButtonHolder>
            </div>
          </div>
        </section>
      </>
    </Page>
  )
}

export const HelpText = styled.div`
  color: white;
  font-size: 3rem;
  line-height: 3rem;

  width: 50%;

  @media (max-width: 991px) {
    width: 100%;
    text-align: center;
  }
`

export const HelpButtonHolder = styled.div`
  width: 50%;

  @media (max-width: 991px) {
    width: 100%;
    margin-top: 35px;
  }
`

export const HelpButton = styled(LocalLink)`
  width: 100%;
  background: #ffffff;
  color: #f7757a;
  font-weight: bold;
  font-size: 1.4rem;
  margin-bottom: 5px;

  & > svg {
    margin-right: auto;
  }

  & > span {
    margin-right: auto;
  }

  &:hover {
    background: var(--clr-gradient-14);
    color: #ffffff;
  }

  @media (max-width: 440px) {
    & > span {
      font-size: 1.1rem;
    }
  }
`

export const PromotionHolder = styled.div`
  background: #f7426f;
  color: white;
  width: 100%;
  padding: 15px;
  display: flex;
  text-align: center;
  justify-content: center;
  align-items: center;
  font-size: 1.2rem;
  flex-direction: column;

  & > a {
    background: white;
    border-radius: 6px;
    padding: 5px 15px;
    margin-top: 5px;
    display: block;
    max-width: 250px;
  }
`

const TestimonialsGrid = styled.div`
  display: grid;
  gap: 12px;
  grid-template-columns: 1fr;

  @media (min-width: 992px) {
    grid-template-columns: repeat(3, 1fr);
  }
`

const TestiCard = styled.blockquote`
  background: #f5f5f5;
  border-radius: 8px;
  padding: 16px;
  margin: 0;
  font-style: italic;
`

const TestiAuthor = styled.div`
  margin-top: 8px;
  font-style: normal;
  font-weight: 600;
  opacity: 0.9;
`
